import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, ChevronLeft, Upload, X, Check, Save, ClipboardList, Search, UserCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLANK_FORM = {
  title: "", description: "", image_data: null,
  event_date: "", event_time: "10:00", location: "",
  ticket_price: 0, total_tickets: 30, status: "active",
  min_age_date: "", max_age_date: "",
};

const fmtDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(y, m - 1, day);
  return dt.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const fmtAgeDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-").map(Number);
  return `${String(day).padStart(2,"0")}/${String(m).padStart(2,"0")}/${y}`;
};

export default function EventsAdminTab({ pin }) {
  const [events, setEvents]       = useState([]);
  const [view, setView]           = useState("list");
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(BLANK_FORM);
  const [bookings, setBookings]   = useState([]);
  const [bookingEvent, setBookingEvent] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [regEvent, setRegEvent]   = useState(null);
  const [regBookings, setRegBookings] = useState([]);
  const [regSearch, setRegSearch] = useState("");

  const loadEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/events?pin=${pin}`);
      setEvents(res.data);
    } catch { toast.error("Failed to load events"); }
  }, [pin]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const openNew  = () => { setEditing(null); setForm(BLANK_FORM); setView("form"); };
  const openEdit = (ev) => { setEditing(ev); setForm({ ...BLANK_FORM, ...ev }); setView("form"); };

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setForm(p => ({ ...p, image_data: ev.target.result }));
    reader.readAsDataURL(file);
  };

  const saveEvent = async () => {
    if (!form.title.trim() || !form.event_date) { toast.error("Title and date are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, fields: [] };
      if (editing) {
        await axios.put(`${API}/admin/events/${editing.id}?pin=${pin}`, payload);
        toast.success("Event updated");
      } else {
        await axios.post(`${API}/admin/events?pin=${pin}`, payload);
        toast.success("Event created");
      }
      await loadEvents();
      setView("list");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save event"); }
    setSaving(false);
  };

  const deleteEvent = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? All bookings will also be removed.`)) return;
    try {
      await axios.delete(`${API}/admin/events/${ev.id}?pin=${pin}`);
      toast.success("Event deleted");
      loadEvents();
    } catch { toast.error("Failed to delete"); }
  };

  const viewBookings = async (ev) => {
    setBookingEvent(ev);
    try {
      const res = await axios.get(`${API}/admin/events/${ev.id}/bookings?pin=${pin}`);
      setBookings(res.data);
      setView("bookings");
    } catch { toast.error("Failed to load bookings"); }
  };

  const startRegistration = async (ev) => {
    setRegEvent(ev);
    setRegSearch("");
    try {
      const res = await axios.get(`${API}/admin/events/${ev.id}/bookings?pin=${pin}`);
      // Only show confirmed (paid or free) bookings
      const confirmed = res.data.filter(b => b.payment_status === "paid" || b.payment_status === "free");
      // Sort alphabetically by guardian full name
      confirmed.sort((a, b) => {
        const nameA = (a.responses?.guardian_full_name || "").toLowerCase();
        const nameB = (b.responses?.guardian_full_name || "").toLowerCase();
        return nameA.localeCompare(nameB);
      });
      setRegBookings(confirmed);
      setView("registration");
    } catch { toast.error("Failed to load bookings"); }
  };

  const toggleCheckIn = async (booking) => {
    const newState = !booking.checked_in;
    // Optimistic update
    setRegBookings(prev => prev.map(b => b.id === booking.id ? { ...b, checked_in: newState } : b));
    try {
      await axios.put(
        `${API}/admin/events/${regEvent.id}/bookings/${booking.id}/checkin?pin=${pin}`,
        { checked_in: newState }
      );
    } catch {
      // Revert on failure
      setRegBookings(prev => prev.map(b => b.id === booking.id ? { ...b, checked_in: !newState } : b));
      toast.error("Failed to update check-in");
    }
  };

  // ── REGISTRATION VIEW ─────────────────────────────────────────────────────
  if (view === "registration" && regEvent) {
    const checkedInCount = regBookings.filter(b => b.checked_in).length;
    const totalChildren  = regBookings.reduce((s, b) => s + (b.attendee_count || 1), 0);

    const query = regSearch.trim().toLowerCase();
    const filtered = query
      ? regBookings.filter(b => {
          const guardian = (b.responses?.guardian_full_name || "").toLowerCase();
          const children = (b.responses?.children || [])
            .map(c => `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase())
            .join(" ");
          return guardian.includes(query) || children.includes(query);
        })
      : regBookings;

    return (
      <div data-testid="registration-view">
        {/* Header */}
        <button onClick={() => setView("list")}
          className="flex items-center gap-1 text-[#636E72] hover:text-[#2D3436] mb-4 text-sm font-semibold"
          data-testid="reg-back-btn">
          <ChevronLeft className="w-4 h-4" /> Back to events
        </button>

        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-[#2D3436]" style={{ fontFamily: "Fredoka, cursive" }}>
              Registration — {regEvent.title}
            </h2>
            <p className="text-xs text-[#636E72] mt-0.5">
              {fmtDate(regEvent.event_date)}{regEvent.event_time ? ` · ${regEvent.event_time}` : ""}
            </p>
          </div>
          <div className="text-right flex-shrink-0 ml-4">
            <p className="text-2xl font-bold text-[#ff3131]" style={{ fontFamily: "Fredoka, cursive" }}>
              {checkedInCount}<span className="text-base text-[#636E72] font-normal"> / {regBookings.length}</span>
            </p>
            <p className="text-[10px] text-[#636E72]">bookings checked in</p>
            <p className="text-[10px] text-[#636E72]">{totalChildren} child{totalChildren !== 1 ? "ren" : ""} total</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#636E72]" />
          <input
            value={regSearch}
            onChange={e => setRegSearch(e.target.value)}
            placeholder="Search by child or guardian name…"
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
            data-testid="reg-search-input"
          />
          {regSearch && (
            <button onClick={() => setRegSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#636E72] hover:text-[#2D3436]">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Attendee list */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 text-[#636E72] text-sm">
            {regBookings.length === 0 ? "No confirmed bookings yet" : "No matches found"}
          </div>
        ) : (
          <div className="space-y-2" data-testid="reg-attendee-list">
            {filtered.map(b => {
              const children = b.responses?.children || [];
              const guardian = b.responses?.guardian_full_name || "Unknown guardian";
              const isCheckedIn = !!b.checked_in;

              return (
                <div key={b.id}
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                    isCheckedIn
                      ? "bg-green-50 border-green-200"
                      : "bg-white border-gray-100 hover:border-gray-200"
                  }`}
                  data-testid={`reg-booking-${b.id}`}>

                  {/* Check-in button */}
                  <button
                    onClick={() => toggleCheckIn(b)}
                    className={`flex-shrink-0 w-9 h-9 rounded-full border-2 flex items-center justify-center transition-colors ${
                      isCheckedIn
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-gray-300 text-gray-300 hover:border-green-400 hover:text-green-400"
                    }`}
                    data-testid={`checkin-btn-${b.id}`}
                    title={isCheckedIn ? "Mark as not arrived" : "Mark as arrived"}>
                    <UserCheck className="w-4 h-4" />
                  </button>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    {children.length > 0 ? (
                      <p className="text-sm font-bold text-[#2D3436] truncate">
                        {children.map(c => `${c.first_name || ""} ${c.last_name || ""}`.trim()).join(", ")}
                      </p>
                    ) : (
                      <p className="text-sm font-bold text-[#2D3436]">{b.booking_ref}</p>
                    )}
                    <p className="text-xs text-[#636E72] truncate">{guardian}</p>
                  </div>

                  {/* Right-side info */}
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-mono font-bold text-[#ff3131]">{b.booking_ref}</p>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      b.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {b.payment_status === "free" ? "Free" : "Paid"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── BOOKINGS VIEW ──────────────────────────────────────────────────────────
  if (view === "bookings" && bookingEvent) {

    const deleteBooking = async (b) => {
      if (!window.confirm(`Delete booking ${b.booking_ref}? This will free up ${b.attendee_count} ticket(s).`)) return;
      try {
        await axios.delete(`${API}/admin/events/${bookingEvent.id}/bookings/${b.id}?pin=${pin}`);
        toast.success("Booking deleted — tickets freed");
        setBookings(prev => prev.filter(x => x.id !== b.id));
      } catch { toast.error("Failed to delete booking"); }
    };

    const startEditBooking = (b) => setEditingBooking({ ...b, responses: { ...b.responses, children: [...(b.responses?.children || [])] } });

    const saveBookingEdit = async () => {
      try {
        await axios.put(
          `${API}/admin/events/${bookingEvent.id}/bookings/${editingBooking.id}?pin=${pin}`,
          { responses: editingBooking.responses }
        );
        toast.success("Booking updated");
        setBookings(prev => prev.map(x => x.id === editingBooking.id ? { ...x, responses: editingBooking.responses } : x));
        setEditingBooking(null);
      } catch { toast.error("Failed to save changes"); }
    };

    const updateEditChild = (i, field, val) =>
      setEditingBooking(prev => ({
        ...prev,
        responses: {
          ...prev.responses,
          children: prev.responses.children.map((c, idx) => idx === i ? { ...c, [field]: val } : c),
        },
      }));

    const updateEditGuardian = (field, val) =>
      setEditingBooking(prev => ({
        ...prev,
        responses: { ...prev.responses, [field]: val },
      }));

    return (
      <div>
        <button onClick={() => { setView("list"); setEditingBooking(null); }}
          className="flex items-center gap-1 text-[#636E72] hover:text-[#2D3436] mb-4 text-sm font-semibold">
          <ChevronLeft className="w-4 h-4" /> Back to events
        </button>
        <h2 className="text-lg font-bold text-[#2D3436] mb-1" style={{ fontFamily: "Fredoka, cursive" }}>
          Bookings — {bookingEvent.title}
        </h2>
        <p className="text-xs text-[#636E72] mb-4">
          {bookings.length} booking{bookings.length !== 1 ? "s" : ""} · {bookingEvent.tickets_sold} ticket{bookingEvent.tickets_sold !== 1 ? "s" : ""} sold
        </p>

        {bookings.length === 0 ? (
          <p className="text-sm text-[#636E72] py-8 text-center">No bookings yet</p>
        ) : (
          <div className="space-y-4">
            {bookings.map(b => {
              const isEditing = editingBooking?.id === b.id;
              const r = isEditing ? editingBooking.responses : (b.responses || {});
              const children = r.children || [];

              return (
                <div key={b.id} className={`bg-white border rounded-xl p-4 ${isEditing ? "border-[#ff3131] ring-1 ring-[#ff3131]/20" : "border-gray-100"}`}
                  data-testid={`booking-${b.id}`}>
                  {/* Header row */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <span className="font-mono text-sm font-bold text-[#ff3131]">{b.booking_ref}</span>
                      <span className="text-xs text-[#636E72] ml-2">× {b.attendee_count} child{b.attendee_count !== 1 ? "ren" : ""}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right mr-1">
                        <p className="font-bold text-[#2D3436] text-sm">
                          {b.total_price > 0 ? `£${b.total_price.toFixed(2)}` : "Free"}
                        </p>
                        <p className="text-[10px] text-[#636E72]">{new Date(b.created_at).toLocaleDateString("en-GB")}</p>
                      </div>
                      {!isEditing && (
                        <button onClick={() => startEditBooking(b)}
                          className="p-1.5 rounded-lg bg-[#ff3131]/10 text-[#ff3131] hover:bg-[#ff3131]/20"
                          title="Edit booking" data-testid={`edit-booking-${b.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => deleteBooking(b)}
                        className="p-1.5 rounded-lg bg-gray-100 text-[#636E72] hover:bg-red-100 hover:text-red-500"
                        title="Delete booking" data-testid={`delete-booking-${b.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Children */}
                  {children.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-[#636E72] uppercase tracking-wider mb-2">Children</p>
                      <div className="space-y-2">
                        {children.map((c, i) => (
                          <div key={i} className="grid grid-cols-3 gap-2">
                            {isEditing ? (
                              <>
                                <input value={c.first_name || ""} onChange={e => updateEditChild(i, "first_name", e.target.value)}
                                  placeholder="First name"
                                  className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                                <input value={c.last_name || ""} onChange={e => updateEditChild(i, "last_name", e.target.value)}
                                  placeholder="Last name"
                                  className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                                <input type="date" value={c.dob || ""} onChange={e => updateEditChild(i, "dob", e.target.value)}
                                  className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                              </>
                            ) : (
                              <div className="col-span-3 flex gap-4 text-xs text-[#636E72]">
                                <span className="font-semibold text-[#2D3436]">{c.first_name} {c.last_name}</span>
                                {c.dob && <span>DOB: {c.dob}</span>}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Guardian */}
                  <div className="mb-3">
                    <p className="text-[10px] font-bold text-[#636E72] uppercase tracking-wider mb-2">Parent / Guardian</p>
                    {isEditing ? (
                      <div className="grid grid-cols-3 gap-2">
                        <input value={r.guardian_full_name || ""} onChange={e => updateEditGuardian("guardian_full_name", e.target.value)}
                          placeholder="Full name"
                          className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                        <input value={r.guardian_email || ""} onChange={e => updateEditGuardian("guardian_email", e.target.value)}
                          placeholder="Email"
                          className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                        <input value={r.guardian_phone || ""} onChange={e => updateEditGuardian("guardian_phone", e.target.value)}
                          placeholder="Phone"
                          className="p-2 border border-gray-200 rounded-lg text-xs focus:border-[#ff3131] outline-none" />
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-[#636E72]">
                        {r.guardian_full_name && <div><span className="font-semibold text-[#2D3436]">Name:</span> {r.guardian_full_name}</div>}
                        {r.guardian_email    && <div><span className="font-semibold text-[#2D3436]">Email:</span> {r.guardian_email}</div>}
                        {r.guardian_phone    && <div><span className="font-semibold text-[#2D3436]">Phone:</span> {r.guardian_phone}</div>}
                      </div>
                    )}
                  </div>

                  {/* Signature (read-only) */}
                  {!isEditing && r.signature && (
                    <div className="mb-3">
                      <p className="text-[10px] font-bold text-[#636E72] uppercase tracking-wider mb-1">Signature</p>
                      <img src={r.signature} alt="Signature" className="h-12 border border-gray-100 rounded bg-white" />
                    </div>
                  )}

                  {/* Payment status */}
                  <div className="flex items-center justify-between mt-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                      b.payment_status === "paid" || b.payment_status === "free"
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {b.payment_status === "free" ? "Free" : b.payment_status}
                    </span>
                    {isEditing && (
                      <div className="flex gap-2">
                        <button onClick={() => setEditingBooking(null)}
                          className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg text-[#636E72] hover:bg-gray-50">
                          Cancel
                        </button>
                        <button onClick={saveBookingEdit}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#ff3131] text-white rounded-lg hover:bg-[#e62e2e]"
                          data-testid={`save-booking-${b.id}`}>
                          <Save className="w-3 h-3" /> Save
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── FORM VIEW ──────────────────────────────────────────────────────────────
  if (view === "form") {
    return (
      <div>
        <button onClick={() => setView("list")} className="flex items-center gap-1 text-[#636E72] hover:text-[#2D3436] mb-4 text-sm font-semibold">
          <ChevronLeft className="w-4 h-4" /> Back to events
        </button>
        <h2 className="text-lg font-bold text-[#2D3436] mb-5" style={{ fontFamily: "Fredoka, cursive" }}>
          {editing ? "Edit Event" : "New Event"}
        </h2>

        <div className="space-y-4 max-w-2xl">
          {/* Event Details */}
          <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-4">
            <h3 className="font-bold text-[#2D3436] text-sm">Event Details</h3>

            <div>
              <label className="text-xs font-semibold text-[#636E72] mb-1 block">Title *</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. Easter Holiday Camp 2025"
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                data-testid="event-title-input" />
            </div>

            <div>
              <label className="text-xs font-semibold text-[#636E72] mb-1 block">Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={4} placeholder="Tell attendees about the event..."
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none resize-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">Date *</label>
                <input type="date" value={form.event_date} onChange={e => setForm(p => ({ ...p, event_date: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="event-date-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">Time</label>
                <input type="time" value={form.event_time} onChange={e => setForm(p => ({ ...p, event_time: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#636E72] mb-1 block">Location</label>
              <input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))}
                placeholder="e.g. Hamble Sports Complex"
                className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">Ticket Price (£)</label>
                <input type="number" min="0" step="0.50" value={form.ticket_price}
                  onChange={e => setForm(p => ({ ...p, ticket_price: parseFloat(e.target.value) || 0 }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="event-price-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">Total Tickets</label>
                <input type="number" min="1" value={form.total_tickets}
                  onChange={e => setForm(p => ({ ...p, total_tickets: parseInt(e.target.value) || 1 }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="event-tickets-input" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-[#636E72]">Status</label>
              <button type="button"
                onClick={() => setForm(p => ({ ...p, status: p.status === "active" ? "draft" : "active" }))}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${form.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-[#636E72]"}`}>
                {form.status === "active" ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                {form.status === "active" ? "Active" : "Draft"}
              </button>
            </div>
          </div>

          {/* Age Range */}
          <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-4">
            <div>
              <h3 className="font-bold text-[#2D3436] text-sm">Age Requirements</h3>
              <p className="text-xs text-[#636E72] mt-0.5">Set date of birth limits for children attending this event. Leave blank if there is no age restriction.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                  Minimum Age
                  <span className="font-normal ml-1">(child's DOB on or before)</span>
                </label>
                <input type="date" value={form.min_age_date}
                  onChange={e => setForm(p => ({ ...p, min_age_date: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="event-min-age-input" />
                {form.min_age_date && (
                  <p className="text-[11px] text-[#636E72] mt-1">Born on or before {fmtAgeDate(form.min_age_date)}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-[#636E72] mb-1 block">
                  Maximum Age
                  <span className="font-normal ml-1">(child's DOB on or after)</span>
                </label>
                <input type="date" value={form.max_age_date}
                  onChange={e => setForm(p => ({ ...p, max_age_date: e.target.value }))}
                  className="w-full p-3 border border-gray-200 rounded-xl text-sm focus:border-[#ff3131] outline-none"
                  data-testid="event-max-age-input" />
                {form.max_age_date && (
                  <p className="text-[11px] text-[#636E72] mt-1">Born on or after {fmtAgeDate(form.max_age_date)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Event Image */}
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <h3 className="font-bold text-[#2D3436] text-sm mb-3">Event Image</h3>
            {form.image_data ? (
              <div className="relative">
                <img src={form.image_data} alt="Event" className="w-full h-48 object-cover rounded-xl" />
                <button onClick={() => setForm(p => ({ ...p, image_data: null }))}
                  className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 shadow text-[#ff3131] hover:bg-red-50">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-[#ff3131] transition-colors">
                <Upload className="w-6 h-6 text-[#636E72] mb-2" />
                <span className="text-sm text-[#636E72]">Click to upload image</span>
                <input type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid="event-image-input" />
              </label>
            )}
          </div>

          <div className="flex gap-3 pb-8">
            <button onClick={() => setView("list")} className="flex-1 py-3 border-2 border-gray-200 rounded-full text-sm font-bold text-[#636E72] hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={saveEvent} disabled={saving}
              className="flex-1 py-3 bg-[#ff3131] hover:bg-[#e62e2e] text-white rounded-full text-sm font-bold disabled:opacity-60 transition-colors"
              data-testid="event-save-btn">
              {saving ? "Saving…" : editing ? "Update Event" : "Create Event"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#636E72]">{events.length} event{events.length !== 1 ? "s" : ""}</p>
        <button onClick={openNew}
          className="flex items-center gap-2 bg-[#ff3131] hover:bg-[#e62e2e] text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
          data-testid="add-event-btn">
          <Plus className="w-4 h-4" /> Add Event
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16 text-[#636E72] text-sm">
          No events yet — click "Add Event" to create your first one
        </div>
      ) : (
        <div className="space-y-3">
          {events.map(ev => (
            <div key={ev.id} className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid={`admin-event-${ev.id}`}>
              <div className="flex gap-4 p-4">
                {ev.image_data && (
                  <img src={ev.image_data} alt={ev.title} className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-[#2D3436] text-sm truncate" style={{ fontFamily: "Fredoka, cursive" }}>{ev.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0 ${ev.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-[#636E72]"}`}>
                      {ev.status}
                    </span>
                  </div>
                  <p className="text-xs text-[#636E72]">{fmtDate(ev.event_date)}{ev.event_time ? ` · ${ev.event_time}` : ""}</p>
                  {ev.location && <p className="text-xs text-[#636E72]">{ev.location}</p>}
                  {(ev.min_age_date || ev.max_age_date) && (
                    <p className="text-xs text-[#636E72]">
                      Ages: {ev.min_age_date ? `DOB ≤ ${fmtAgeDate(ev.min_age_date)}` : ""}
                      {ev.min_age_date && ev.max_age_date ? " · " : ""}
                      {ev.max_age_date ? `DOB ≥ ${fmtAgeDate(ev.max_age_date)}` : ""}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-xs font-bold text-[#ff3131]">{ev.ticket_price > 0 ? `£${ev.ticket_price.toFixed(2)}` : "Free"}</span>
                    <span className="text-xs text-[#636E72]">{ev.tickets_remaining} / {ev.total_tickets} remaining</span>
                    <span className="text-xs text-[#636E72]">{ev.bookings_count} booking{ev.bookings_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button onClick={() => startRegistration(ev)}
                    className="p-2 rounded-lg bg-green-100 text-green-600 hover:bg-green-200"
                    title="Start registration / check-in" data-testid={`start-reg-${ev.id}`}>
                    <ClipboardList className="w-4 h-4" />
                  </button>
                  <button onClick={() => viewBookings(ev)} className="p-2 rounded-lg bg-[#7dbff8]/10 text-[#7dbff8] hover:bg-[#7dbff8]/20" title="View bookings" data-testid={`view-bookings-${ev.id}`}>
                    <Eye className="w-4 h-4" />
                  </button>
                  <button onClick={() => openEdit(ev)} className="p-2 rounded-lg bg-[#ff3131]/10 text-[#ff3131] hover:bg-[#ff3131]/20" title="Edit" data-testid={`edit-event-${ev.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => deleteEvent(ev)} className="p-2 rounded-lg bg-gray-100 text-[#636E72] hover:bg-red-100 hover:text-red-500" title="Delete" data-testid={`delete-event-${ev.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
